<!--
/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */
-->
<template>
  <!-- Description (so far only seen for Birthdays calendar) -->
  <div class="form-group" v-if="extendedProperties.description?.value">
    <div class="font-bold mb-4">{{ i18n.description }}</div>
    <div class="text-gray">{{ extendedProperties.description.value }}</div>
  </div>
  <!-- Module -->
  <div class="form-group">
    <label class="control-label" :for="typeId">{{ i18n.folderType }}</label>
    <input type="text" class="form-control" :id="typeId" readonly :value="folderType">
  </div>
  <!-- Total -->
  <div class="form-group" v-if="folderTotal">
    <label class="control-label" :for="totalId">{{ i18n.folderTotal }}</label>
    <input type="text" class="form-control" :id="totalId" readonly :value="folderTotal">
  </div>
  <!-- Creation -->
  <div class="form-group" v-if="folderCreation">
    <label class="control-label" :for="creationId">{{ i18n.folderCreation }}</label>
    <input type="text" class="form-control" :id="creationId" readonly :value="folderCreation">
  </div>
  <!-- Permissions -->
  <div class="form-group">
    <label class="control-label" :for="permissionId">{{ i18n.folderPermissions }}</label>
    <input type="text" class="form-control" :id="permissionId" readonly :value="folderPermissions">
  </div>
  <!-- iCal URL (incoming data) -->
  <div class="form-group" v-if="icalUrl">
    <label class="control-label" :for="icalId">{{ i18n.ical }}</label>
    <div class="input-group link-group">
      <input type="text" class="form-control" :id="icalId" readonly :value="icalUrl">
      <span class="input-group-btn" ref="copyIcal"/>
    </div>
  </div>
  <!-- Last update -->
  <div class="form-group" v-if="extendedProperties.lastUpdate">
    <label class="control-label" :for="lastUpdateId">{{ i18n.lastUpdate }}</label>
    <input type="text" class="form-control" :id="lastUpdateId" readonly :value="moment(extendedProperties.lastUpdate.value).fromNow()">
  </div>
  <!-- CardDAV or CalDAV -->
  <template v-if="supportsDav">
    <div class="form-group">
      <label class="control-label" :for="davId">{{ davLabel.url }}</label>
      <div class="input-group link-group">
        <input type="text" class="form-control" :id="davId" readonly :value="davUrl || i18n.notAvailable">
        <span class="input-group-btn" ref="copyDav" :class="{ hidden: !davUrl }"/>
      </div>
    </div>
    <div class="form-group">
      <div class="checkbox custom small">
        <label class="control-label">
          <input type="checkbox" name="sync" :checked="folder.used_for_sync.value === 'true'" :disabled="folder.used_for_sync.protected === 'true'" @change="toggleSync"/>
          {{ davLabel.checkbox }}
        </label>
      </div>
    </div>
  </template>
</template>

<script setup>
import ox from '@/ox'
import $ from '@/jquery'
import { useFolderStore } from '@/io.ox/core/folder/store.js'
import { computed, onMounted, ref, useId, watch } from 'vue'
import CopyToClipboard from '@/io.ox/backbone/mini-views/copy-to-clipboard'
import { getFullName } from '@/io.ox/contacts/util'
import folderApi from '@/io.ox/core/folder/api'
import capabilities from '@/io.ox/core/capabilities'
import gt from 'gettext'
import moment from '@/moment'

const i18n = {
  description: gt('Description'),
  ical: gt('iCal URL'),
  // #. a dialog label "[Folder] Creation (on and by): ..."
  folderCreation: gt('Created'),
  // #. Folder permissions (e.g. read, write, delete [items])
  folderPermissions: gt('Permissions'),
  folderTotal: gt('Message count'),
  // #. Folder type (email, calendar, address book, ...)
  folderType: gt('Folder type'),
  lastUpdate: gt('Last update'),
  notAvailable: gt('Not available'),
  syncViaDAV: gt('Sync via DAV')
}

const { folderId } = defineProps({
  folderId: { type: String, required: true }
})

const copyIcal = ref(null)
const copyDav = ref(null)
const store = useFolderStore()
const folder = store.get(folderId)

const typeId = useId()
const totalId = useId()
const creationId = useId()
const permissionId = useId()
const icalId = useId()
const lastUpdateId = useId()
const davId = useId()

const providerNames = {
  ical: gt('iCal feed'),
  google: gt('Google subscription')
}

const folderType = computed(() => {
  switch (folder.value.module) {
    case 'mail': return gt('Email')
    case 'calendar': {
      const type = gt('Calendar')
      const provider = folder.value['com.openexchange.calendar.provider']
      return (provider && providerNames[provider])
        ? `${type} (${providerNames[provider]})`
        : type
    }
    case 'contacts': return gt('Address Book')
    case 'tasks': return gt('Tasks')
    case 'infostore': return gt.pgettext('app', 'Drive')
    default: return 'N/A'
  }
})

const folderTotal = computed(() => {
  let total = folder.value.total !== null ? String(folder.value.total) : ''
  // #. %1$d is number of unread messages
  if (folder.value.unread !== null && folder.value.total > 0) total += ` (${gt('%1$d unread', folder.value.unread)})`
  return total
})

const folderCreation = computed(() => {
  const date = folder.value.creation_date
    ? moment(folder.value.creation_date).format('LLL')
    : ''
  const createdBy = folder.value.created_from?.contact && folder.value.created_from?.display_name !== 'context admin'
    ? getFullName(folder.value.created_from.contact)
    : ''
  return [date, createdBy].filter(Boolean).join(' - ')
})

const folderPermissions = computed(() => {
  return [
    // #. permissions: read access (verb)
    folderApi.can('read', folder.value) ? gt('Read') : '',
    // #. permissions: create access (verb), e.g. "Erstellen" in German
    folderApi.can('create', folder.value) ? gt('Create') : '',
    // #. permissions: edit/write access (verb), e.g. "Bearbeiten" in German
    folderApi.can('write', folder.value) ? gt('Edit') : '',
    // #. permissions: delete access (verb)
    folderApi.can('delete', folder.value) ? gt('Delete') : '',
    // #. permissions: Rename (folder)
    folderApi.can('rename', folder.value) ? gt('Rename folder') : '',
    // #. permissions: Change permissions
    folderApi.can('changepermissions', folder.value) ? gt('Change permissions') : '',
  ]
    // some folders are visible (e.g. Public folder in Drive) but have none of the above permissions
    .filter(Boolean).join(', ') || gt('None')
})

const extendedProperties = computed(() => {
  return folder.value['com.openexchange.calendar.extendedProperties'] || {}
})

const icalUrl = computed(() => {
  if (folder.value['com.openexchange.calendar.provider'] !== 'ical') return ''
  return folder.value['com.openexchange.calendar.config']?.uri || ''
})

const supportsDav = computed(() => {
  switch (folder.value.module) {
    case 'contacts': return capabilities.has('carddav')
    case 'calendar':
    case 'tasks': return capabilities.has('caldav')
    default: return false
  }
})

const davLabel = computed(() => {
  return folder.value.module === 'contacts'
    ? {
        url: gt('CardDAV URL'),
        checkbox: gt('Enable CardDAV synchronization for this folder')
      }
    : {
        url: gt('CalDAV URL'),
        checkbox: gt('Enable CalDAV synchronization for this folder')
      }
})

const davUrl = computed(() => {
  const type = folder.value.module === 'contacts' ? 'carddav' : 'caldav'
  return (folder.value.used_for_sync.value === 'true' && folder.value[`com.openexchange.${type}.url`]) || ''
})

const copyIcalUrl = new CopyToClipboard({ content: icalUrl.value })
const copyDavUrl = new CopyToClipboard({ content: davUrl.value })

watch(() => icalUrl.value, () => copyIcalUrl.changeClipboardText(icalUrl.value))
watch(() => davUrl.value, () => copyDavUrl.changeClipboardText(davUrl.value))

async function toggleSync (e) {
  const enabled = e.target.checked
  await store.setUsedForSync(folderId, enabled)
}

onMounted(async () => {
  try {
    await store.fetch(folderId)
    if (ox.debug) console.debug('Folder data', folder.value)
    if (copyIcal.value) $(copyIcal.value).append(copyIcalUrl.render().$el)
    if (copyDav.value) $(copyDav.value).append(copyDavUrl.render().$el)
  } catch (err) {
    console.error(err)
  }
})

</script>
